import { getLocalTimeZone, parseDate, today, type DateValue } from '@internationalized/date'
import { useMemo, useState } from 'react'

type CustomCalendarProps = {
  isOpen: boolean
  onClose: () => void
  onReset: () => void
  minDate?: DateValue
  maxDate?: DateValue
  isDateUnavailable?: (date: DateValue) => boolean
  onDateSelect: (date: DateValue) => void
  currentValue?: string
  locale?: string // Add locale prop
}

export const CustomCalendar = ({
  isOpen,
  onClose,
  onReset,
  minDate,
  maxDate,
  isDateUnavailable,
  onDateSelect,
  currentValue,
  locale = 'en-US' // Default to en-US
}: CustomCalendarProps) => {
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = today(getLocalTimeZone())
    return { year: now.year, month: now.month }
  })

  // Localized month names
  const monthNames = useMemo(() => {
    const dtf = new Intl.DateTimeFormat(locale, { month: 'long' })
    return Array.from({ length: 12 }, (_, i) => dtf.format(new Date(2000, i, 1)))
  }, [locale])

  // Localized day names (short)
  const dayNames = useMemo(() => {
    const dtf = new Intl.DateTimeFormat(locale, { weekday: 'short' })
    // Sunday = 0 ... Saturday = 6
    return Array.from({ length: 7 }, (_, i) => dtf.format(new Date(2000, 0, 2 + i)))
  }, [locale])

  const getDaysInMonth = (year: number, month: number) => {
    return new Date(year, month, 0).getDate()
  }

  const getFirstDayOfMonth = (year: number, month: number) => {
    return new Date(year, month - 1, 1).getDay()
  }
  const isDateDisabled = (year: number, month: number, day: number) => {
    try {
      const date = parseDate(
        `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
      )

      if (minDate && date.compare(minDate) < 0) return true
      if (maxDate && date.compare(maxDate) > 0) return true

      // Check if date is unavailable based on custom logic
      if (isDateUnavailable && isDateUnavailable(date)) return true

      return false
    } catch {
      return true
    }
  }

  const isDateSelected = (year: number, month: number, day: number) => {
    if (!currentValue) return false
    const dateString = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
    return currentValue === dateString
  }

  const handleDateClick = (year: number, month: number, day: number) => {
    if (isDateDisabled(year, month, day)) return

    try {
      const date = parseDate(
        `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
      )
      onDateSelect(date)
      onClose()
    } catch (error) {
      console.error('Error selecting date:', error)
    }
  }

  const goToPreviousMonth = () => {
    setCurrentMonth((prev) => {
      if (prev.month === 1) {
        return { year: prev.year - 1, month: 12 }
      }
      return { year: prev.year, month: prev.month - 1 }
    })
  }

  const goToNextMonth = () => {
    setCurrentMonth((prev) => {
      if (prev.month === 12) {
        return { year: prev.year + 1, month: 1 }
      }
      return { year: prev.year, month: prev.month + 1 }
    })
  }

  const renderCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentMonth.year, currentMonth.month)
    const firstDay = getFirstDayOfMonth(currentMonth.year, currentMonth.month)
    const days = []

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className='h-8 w-8'></div>)
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const disabled = isDateDisabled(currentMonth.year, currentMonth.month, day)
      const selected = isDateSelected(currentMonth.year, currentMonth.month, day)

      days.push(
        <button
          key={day}
          type='button'
          onClick={() => handleDateClick(currentMonth.year, currentMonth.month, day)}
          disabled={disabled}
          className={`flex h-8 w-8 items-center justify-center rounded-full text-sm transition-colors ${disabled
            ? 'cursor-not-allowed bg-gray-100 text-gray-300'
            : 'cursor-pointer text-gray-700 hover:bg-blue-100'
            } ${selected ? 'bg-blue-500 text-white hover:bg-blue-600' : ''} `}>
          {day}
        </button>
      )
    }

    return days
  }

  if (!isOpen) return null

  return (
    <div
      className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50'
      onClick={onClose}>
      <div
        className='mx-4 w-full max-w-sm rounded-lg bg-white p-4 shadow-lg'
        onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className='mb-4 flex items-center justify-between'>
          <button
            type='button'
            onClick={goToPreviousMonth}
            className='rounded p-1 hover:bg-gray-100'>
            &#8249;
          </button>
          <h3 className='text-lg font-semibold'>
            {monthNames[currentMonth.month - 1]} {currentMonth.year}
          </h3>
          <button type='button' onClick={goToNextMonth} className='rounded p-1 hover:bg-gray-100'>
            &#8250;
          </button>
        </div>

        {/* Days of week */}
        <div className='mb-2 grid grid-cols-7 gap-1'>
          {dayNames.map((day) => (
            <div key={day} className='py-2 text-center text-xs font-medium text-gray-500'>
              {day}
            </div>
          ))}
        </div>

        {/* Calendar days */}
        <div className='mb-4 grid grid-cols-7 gap-1'>{renderCalendarDays()}</div>

        {/* Actions */}
        <div className='flex justify-between'>
          <button
            type='button'
            onClick={onReset}
            className='rounded px-4 py-2 text-blue-500 hover:bg-blue-50'>
            Reset
          </button>
          <button
            type='button'
            onClick={onClose}
            className='rounded bg-orange-500 px-4 py-2 text-white hover:bg-orange-600'>
            Done
          </button>
        </div>
      </div>
    </div>
  )
}
